import React from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import AboutNarrative from "../components/about/AboutNarrative";
import MagPresenceMap from "../components/MagPresenceMap";
import { breadcrumbList } from "../seo/breadcrumbSchema";
import { jsonLdWithMedia } from "../seo/mediaSchema";

const challengeStats = [
  {
    value: "100%",
    title: "Operational stability",
    body: "Predictable, 24/7 automated material flow that insulates plants from 35% to 49% frontline turnover and $18,500 in re-hiring cost per lost worker.",
  },
  {
    value: "-100%",
    valueClass: "text-red-600",
    title: "Zero safety incidents",
    body: "360° LiDAR AMRs replace manned forklifts tied to 34,000+ annual injuries, roughly 1 in 10 forklifts in an accident each year, eliminating $190,000+ per-incident exposure.",
  },
  {
    value: "+1.5x to 2.0x",
    title: "Enterprise value",
    body: "Valuation multiples expand from roughly 5.0x to 5.5x for manual plants to 7.0x to 8.5x for automated ones, turning every $100K in permanent labor savings into $700K+ in equity value.",
  },
];

export default function WhoWeAre() {
  return (
    <div className="bg-white text-gray-900">
      <Seo
        title="About MAG | In-House AGV and AMR Integration Team"
        description="MAG's 30+ in-house specialists across three teams have deployed 115 AMRs in some of the toughest brownfield automotive plants. Built in Metro Detroit."
        path="/who-we-are"
        jsonLd={jsonLdWithMedia(
          "/who-we-are",
          breadcrumbList([
            { name: "Home", path: "/" },
            { name: "Who We Are", path: "/who-we-are" },
          ])
        )}
      />

      <section className="relative h-[40vh] overflow-hidden bg-gray-900 sm:h-[55vh]">
        <div className="relative z-10 flex h-full items-end px-4 pb-8 sm:px-6 sm:pb-12 lg:px-8">
          <div className="w-full text-white">
            <p className="page-eyebrow-on-dark">About</p>
            <h1 className="mt-3 max-w-3xl text-3xl font-bold tracking-tight sm:text-5xl">
              The in-house integration team
            </h1>
            <p className="mt-4 max-w-2xl text-base text-white/75 sm:text-lg">
              Built in Metro Detroit.
            </p>
          </div>
        </div>
      </section>

      <AboutNarrative />

      <section className="bg-gray-50 px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div>
          <p className="page-eyebrow">The challenge</p>
          <h2 className="mt-3 max-w-3xl text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
            Beyond ROI
          </h2>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-gray-600">
            Labor, safety, and enterprise value sit behind every automation decision. These are
            industry figures, not MAG project results.
          </p>
          <div className="mt-12 grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-12">
            {challengeStats.map((stat) => (
              <div key={stat.title} className="border-t border-gray-300 pt-5">
                <p className={`text-3xl font-bold tabular-nums tracking-tight sm:text-4xl ${stat.valueClass || "text-gray-900"}`}>
                  {stat.value}
                </p>
                <h3 className="mt-4 text-base font-semibold text-gray-900">{stat.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-gray-600">{stat.body}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 max-w-4xl text-xs leading-relaxed text-gray-400">
            Sources: U.S. Bureau of Labor Statistics (JOLTS); Deloitte and The Manufacturing Institute; PitchBook M&A
            sector valuation benchmarks; McKinsey & Co.; OSHA forklift safety statistics; National
            Safety Council.
          </p>
        </div>
      </section>

      <MagPresenceMap />

      <section className="relative overflow-hidden bg-gray-950 px-4 py-16 text-white sm:px-6 sm:py-20 lg:px-8">
        <div className="absolute inset-0 bg-tech-grid opacity-30" aria-hidden="true" />
        <div className="relative">
          <h2 className="max-w-3xl text-2xl font-bold tracking-tight sm:text-3xl">
            Ready to start a project?
          </h2>
          <p className="mt-4 max-w-xl text-base text-white/75 sm:text-lg">
            Scope through 24/7 support, fit to the floor already in production.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link to="/contact" className="btn-primary">
              Contact Us
            </Link>
            <Link to="/mag-lab" className="btn-secondary">
              Visit the MAG Lab
            </Link>
          </div>
        </div>
      </section>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "Who We Are | Manufacturing Automation Group (MAG)",
            url: "https://www.mag-automate.com/who-we-are",
            mainEntity: {
              "@type": "Organization",
              name: "Manufacturing Automation Group",
              address: {
                "@type": "PostalAddress",
                streetAddress: "35572 Veronica St",
                addressLocality: "Livonia",
                addressRegion: "MI",
                postalCode: "48150",
                addressCountry: "US",
              },
            },
          }),
        }}
      />
    </div>
  );
}
